import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { queryKeys } from "../api/queryKeys";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { routes } from "../routes";
import { CenteredMessage } from "./CenteredMessage";

const NAV = [
  {
    label: "Comptes API",
    path: routes.home(),
    // Icône "utilisateurs" (trait, façon lucide)
    icon: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8 M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75",
  },
];

function currentDateLabel(): string {
  const fmt = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return fmt.format(new Date()).toUpperCase();
}

/**
 * Shell de l'application, calqué sur OneToOne : sidebar d'icônes à gauche, header
 * (date + menu utilisateur), grande carte de contenu. Le contrôle d'admission se fait
 * ici via api.me : sans le droit Grainbow « WsdAdmin », écran "accès refusé" affichant
 * l'id Milo, qui est la clé d'attribution du droit.
 */
export function Layout() {
  const { t, dark, toggleDark } = useTheme();
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);

  const meQ = useQuery({ queryKey: queryKeys.me, queryFn: api.me, retry: false });

  const handleLogout = () => {
    setMenu(false);
    logout();
    navigate(routes.login(), { replace: true });
  };

  const content = () => {
    if (meQ.isLoading) return <CenteredMessage>Chargement…</CenteredMessage>;
    // Un 401 ne passe plus ici : le client API purge la session et le garde AuthRequired
    // redirige vers la connexion. Il ne reste donc que les pannes réelles (réseau,
    // backend indisponible), pour lesquelles se reconnecter ne servirait à rien : on
    // affiche la cause et on propose de réessayer.
    if (meQ.isError)
      return (
        <CenteredMessage tone="error">
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Session non vérifiable</div>
            <div style={{ color: t.text2, marginBottom: 12 }}>
              {(meQ.error as Error).message}
            </div>
            <button onClick={() => meQ.refetch()} style={{
              border: `1px solid ${t.rowBorder}`, background: t.panelBg, color: t.accent,
              borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit",
            }}>Réessayer</button>
          </div>
        </CenteredMessage>
      );
    if (!meQ.data!.isAdmin)
      return (
        <CenteredMessage tone="error">
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Accès refusé</div>
            <div style={{ color: t.text2 }}>
              Ce site est réservé aux porteurs du droit <code>WsdAdmin</code>. Votre id Milo est
              le <b style={{ color: t.text1 }}>{meQ.data!.userId}</b> : transmettez-le à
              l'administrateur des droits Grainbow si cet accès est légitime.
            </div>
          </div>
        </CenteredMessage>
      );
    return <Outlet />;
  };

  return (
    <div style={{
      display: "flex", height: "100vh", width: "100vw", overflow: "hidden",
      background: t.mainBg, color: t.text1,
    }}>
      {/* SIDEBAR */}
      <nav style={{
        width: 64, flex: "none", background: t.sidebarBg, borderRight: `1px solid ${t.divider}`,
        display: "flex", flexDirection: "column", alignItems: "center", padding: "14px 0 18px",
        gap: 2, zIndex: 20, overflow: "hidden",
      }}>
        <div style={{
          width: 42, height: 42, borderRadius: 11,
          background: "linear-gradient(140deg,#330072,#0072CE)",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 22, flex: "none", boxShadow: "0 4px 16px rgba(51,0,114,.35)",
          color: "#fff", fontWeight: 700, fontSize: 15,
        }}>W</div>
        {NAV.map((item) => {
          const active = location.pathname === item.path;
          return (
            <div
              key={item.label}
              title={item.label}
              onClick={() => navigate(item.path)}
              style={{
                width: 44, height: 44, borderRadius: 10,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
                background: active ? t.activeNav : "transparent",
                color: active ? "#ffffff" : t.inactiveNav,
                flex: "none", margin: "1px 0",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d={item.icon} />
              </svg>
            </div>
          );
        })}
      </nav>

      {/* RIGHT SIDE */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* HEADER */}
        <header style={{
          height: 56, flex: "none", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 22px", position: "relative", zIndex: 10,
        }}>
          <div style={{ width: 100, fontSize: 13.5, fontWeight: 700, color: "#fff" }}>WSD Admin</div>
          <div style={{
            fontSize: 12.5, fontWeight: 700, letterSpacing: ".13em",
            textTransform: "uppercase", color: "#fff",
          }}>
            {currentDateLabel()}
          </div>
          <div style={{ width: 100, display: "flex", justifyContent: "flex-end", position: "relative" }}>
            <div
              onClick={() => setMenu(m => !m)}
              title="Menu utilisateur"
              style={{
                width: 38, height: 38, borderRadius: "50%", background: "#0072CE",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", userSelect: "none", flex: "none", color: "#fff",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>

            {menu && (
              <div style={{
                position: "absolute", top: 46, right: 0, background: t.cardBg,
                border: `1px solid ${t.rowBorder}`, borderRadius: 11, padding: "6px 0",
                minWidth: 200, boxShadow: "0 20px 50px rgba(0,0,0,.22)", zIndex: 200,
              }}>
                <div style={{ padding: "10px 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, color: t.text2 }}>Mode sombre</span>
                  <div
                    onClick={toggleDark}
                    style={{
                      position: "relative", width: 42, height: 23, borderRadius: 999,
                      background: dark ? "#303AB2" : "#d1d5db", cursor: "pointer", flex: "none",
                      transition: "background .2s",
                    }}
                  >
                    <div style={{
                      position: "absolute", top: 3.5, left: dark ? 23 : 3,
                      width: 16, height: 16, borderRadius: "50%", background: "#fff",
                      transition: "left .2s", boxShadow: "0 1px 4px rgba(0,0,0,.3)",
                    }} />
                  </div>
                </div>
                <div style={{ height: 1, background: t.divider, margin: "4px 14px" }} />
                <div
                  onClick={handleLogout}
                  style={{ padding: "9px 16px", fontSize: 13, color: t.text2, cursor: "pointer" }}
                >
                  Déconnexion
                </div>
              </div>
            )}
          </div>
        </header>

        {/* MAIN */}
        <main style={{
          flex: 1, overflow: "hidden", padding: "0 18px 18px",
          display: "flex", flexDirection: "column",
        }}>
          <div style={{
            flex: 1, overflow: "hidden", background: t.cardBg, borderRadius: 14,
            border: `1px solid ${t.rowBorder}`, boxShadow: "0 8px 40px rgba(0,0,0,.15)",
            display: "flex",
          }}>
            {content()}
          </div>
        </main>
      </div>
    </div>
  );
}
